import { FieldPath, UseFormReturn } from "react-hook-form"
import { LocalFormSchema } from "../logic/applicationUpdateSchema"

/**
 * Takes a flattened object and returns a nested object.
 * Numeric keys are treated as array indices.
 * Text keys are treated as object keys.
 * We don't support mixed numeric and string keys at the same level.
 * @param flattened - The flattened object to unflatten.
 * @returns The unflattened nested object or array.
 */
export const unflattenObject = (flattened: Record<string, any>): Record<string, any> | Record<string, any>[] => {
  const nested: Record<string, any> = {}
  Object.entries(flattened).forEach(([key, value]) => {
    const keys = key.split('.')
    keys.reduce((acc, k, i) => {
      if (i === keys.length - 1) {
        if (/^\d+$/.test(k)) {
          acc.push(value)
        } else {
          acc[k] = value
        }
      } else {
        if (/^\d+$/.test(keys[i + 1])) {
          acc[k] = acc[k] || []
        } else {
          acc[k] = acc[k] || {}
        }
      }
      return acc[k]
    }, nested)
  })

  // If the nested keys are numeric, convert it to an array
  if (Object.keys(nested).every(k => /^\d+$/.test(k))) {
    return Object.values(nested)
  } else {
    return nested
  }
}

/**
 * Take the React Form Hook `form.formState.errors` and flatten it into a list of error messages.
 */
export const flattenErrors = (errors: any, path: string = ''): {  path: string, message: string }[] => {
  const result: {  path: string, message: string }[] = []
  for (const key in errors) {
    if (errors[key] == null) continue
    if (typeof errors[key].message === 'string') {
      result.push({
        path: path ? `${path}.${key}` : key,
        message: errors[key].message,
      })
    } else {
      result.push(...flattenErrors(errors[key], path ? `${path}.${key}` : key))
    }
  }
  return result
}

/** Human labels for the leaf field names, where splitting the camel case would not read well */
const FIELD_LABELS: Record<string, string> = {
  NiNumber: 'National Insurance number',
  PresentSurname: 'Present surname',
  DateOfBirth: 'Date of birth',
  ContactNumber: 'Contact number',
  LanguagePreference: 'Language preference',
  BirthSurname: 'Birth surname',
  BirthSurnameUntil: 'Birth surname used until',
  BirthTown: 'Birth town',
  BirthCounty: 'Birth county',
  BirthCountry: 'Birth country',
  BirthNationality: 'Birth nationality',
  AddressLine1: 'address line 1',
  AddressLine2: 'address line 2',
  AddressTown: 'town',
  AddressCounty: 'county',
  Postcode: 'postcode',
  CountryCode: 'country',
  ResidentFromGyearMonth: 'resident from',
  ResidentToGyearMonth: 'resident to',
  PassportNumber: 'Passport number',
  PassportDob: 'Passport date of birth',
  PassportIssueDate: 'Passport issue date',
  PassportNationality: 'Passport nationality',
  DriverLicenceNumber: 'Driver licence number',
  DriverLicenceDOB: 'Driver licence date of birth',
  DriverLicenceValidFrom: 'Driver licence valid from',
  DriverLicenceType: 'Driver licence type',
  DriverLicenceIssueCountry: 'Driver licence issuing country',
  UnspentConvictions: 'Unspent convictions',
  DeclarationByApplicant: 'Applicant declaration',
}

const capitalise = (text: string) => text.charAt(0).toUpperCase() + text.slice(1)

/**
 * A label the applicant will recognise for a form error path, e.g.
 * `AdditionalApplicantDetails.ContactNumber` → "Contact number",
 * `Middlenames.Middlename.1` → "Middle name 2",
 * `PreviousAddress.0.Address.Postcode` → "Previous address 1 postcode".
 * Used where an error is listed away from its field (the review page), so the message says what it is about.
 */
export const fieldLabel = (path: string): string => {
  const parts = path.split('.')
  const index = (i: number) => (i >= 0 && /^\d+$/.test(parts[i] ?? '') ? Number(parts[i]) + 1 : undefined)

  if (parts[0] === 'Middlenames') return `Middle name ${index(2) ?? ''}`.trim()
  if (parts[1] === 'OtherForenames') return `Other forename ${index(3) ?? ''}`.trim()
  if (parts[1] === 'OtherSurnames') return `Other surname ${index(3) ?? ''}`.trim()
  if (parts[0] === 'CurrentAddress') return capitalise(`current address ${FIELD_LABELS[parts[parts.length - 1]] ?? ''}`.trim())
  if (parts[0] === 'PreviousAddress') return capitalise(`previous address ${index(1) ?? ''} ${FIELD_LABELS[parts[parts.length - 1]] ?? ''}`.replace(/\s+/g, ' ').trim())

  const leaf = parts[parts.length - 1]
  if (FIELD_LABELS[leaf]) return capitalise(FIELD_LABELS[leaf])
  // Split the camel case: "SomeFieldName" → "Some field name"
  return capitalise(leaf.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase())
}

export const trimTextFields = (form: UseFormReturn<LocalFormSchema>) => {
  const trimField = (key: FieldPath<LocalFormSchema>) => {
    const value = form.getValues(key)
    if (value != null && typeof value === 'string') {
      const trimmedValue = value.trim()
      if (trimmedValue !== value) {
        form.setValue(key, trimmedValue)
        form.trigger(key)
      }
    }
  }

  const fieldsIdentity: FieldPath<LocalFormSchema>[] = [
    'AdditionalApplicantDetails.ContactNumber',
    'NiNumber',
  ]

  const fieldsNames: FieldPath<LocalFormSchema>[] = [
    'Forename',
    ...(form.getValues().Middlenames?.Middlename?.map((_, i) => `Middlenames.Middlename.${i}` as 'Middlenames.Middlename.0') ?? []),
    'PresentSurname',
    'AdditionalApplicantDetails.BirthSurname',
    ...(form.getValues().AdditionalApplicantDetails?.OtherForenames?.OtherForename.map((_, i) => `AdditionalApplicantDetails.OtherForenames.OtherForename.${i}.Name` as 'AdditionalApplicantDetails.OtherForenames.OtherForename.0.Name') ?? []),
    ...(form.getValues().AdditionalApplicantDetails?.OtherSurnames?.OtherSurname.map((_, i) => `AdditionalApplicantDetails.OtherSurnames.OtherSurname.${i}.Name` as 'AdditionalApplicantDetails.OtherSurnames.OtherSurname.0.Name') ?? []),
  ]

  const fieldsBirth: FieldPath<LocalFormSchema>[] = [
    'AdditionalApplicantDetails.BirthTown',
    'AdditionalApplicantDetails.BirthCounty',
    'AdditionalApplicantDetails.BirthNationality',
  ]

  const fieldsAddresses: FieldPath<LocalFormSchema>[] = [
    'CurrentAddress.Address.AddressLine1',
    'CurrentAddress.Address.AddressLine2',
    'CurrentAddress.Address.AddressTown',
    'CurrentAddress.Address.AddressCounty',
    'CurrentAddress.Address.Postcode',
    ...(form.getValues().PreviousAddress?.flatMap((_, i) => [
      `PreviousAddress.${i}.Address.AddressLine1` as 'PreviousAddress.0.Address.AddressLine1',
      `PreviousAddress.${i}.Address.AddressLine2` as 'PreviousAddress.0.Address.AddressLine2',
      `PreviousAddress.${i}.Address.AddressTown` as 'PreviousAddress.0.Address.AddressTown',
      `PreviousAddress.${i}.Address.AddressCounty` as 'PreviousAddress.0.Address.AddressCounty',
      `PreviousAddress.${i}.Address.Postcode` as 'PreviousAddress.0.Address.Postcode',
    ]) ?? []),
  ]

  const fieldsDocuments: FieldPath<LocalFormSchema>[] = [
    'ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceNumber',
    'ApplicantIdentityDetails.PassportDetails.PassportNumber',
    'ApplicantIdentityDetails.PassportDetails.PassportNationality',
  ]

  const fieldsAll: FieldPath<LocalFormSchema>[] = [
    ...fieldsIdentity,
    ...fieldsNames,
    ...fieldsBirth,
    ...fieldsAddresses,
    ...fieldsDocuments,
  ]

  fieldsAll.forEach(trimField)
}

export const ensureUppercase = (form: UseFormReturn<LocalFormSchema>, key: FieldPath<LocalFormSchema>) => {
  const value = form.getValues(key)
  if (value != null && typeof value === 'string') {
    const uppercaseValue = value.toUpperCase()
    if (uppercaseValue !== value) {
      form.setValue(key, uppercaseValue)
      form.trigger(key)
    }
  }
}

export const cleanupDriverLicenceNumber = (form: UseFormReturn<LocalFormSchema>) => {
  const value = form.getValues('ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceNumber')

  // The driver licence number should already be trimmed of whitespace
  // If the driver licence contains a space, remove it and everything else after it
  if (value != null && typeof value === 'string' && value.includes(' ')) {
    const fixedValue = value.split(' ')[0]
    if (fixedValue !== value) {
      form.setValue('ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceNumber', fixedValue)
      form.trigger('ApplicantIdentityDetails.DriverLicenceDetails.DriverLicenceNumber')
    }
  }
}
